/**
 * Worker ↔ Cornerstone Bridge
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Регистрирует файлы, распарсенные воркерами, в Cornerstone imageLoader.
 * [AUDIT FIX] Заполняет metaCache с геометрией для 3D MPR!
 */

import { cornerstone, dicomImageLoader } from '../core/init.js';
import { StudyQualityAnalyzer } from '../storage/studyQualityAnalyzer.js';
import { _setQualityState, showStudyQuality, cacheDataSet } from './loaderManager.js';
import { sortSlices } from './sliceSort.js';
import { log } from '../utils/logger.js';
import { patientsBadgeHTML } from '../ui/statusIcons.js';   // [11.1.6] qualityBadgeHTML убран вместе со значком качества // [7.0.48] SVG-статусы
import { classifySop, classifyCine } from '../core/sopKind.js';            // [10.3.0] снимок или не снимок

// ══════════════════════════════════════════════════════════════
// STATE & LAZY INIT
// ══════════════════════════════════════════════════════════════

let _dicomParser = null;
const _series = new Map();     // seriesUID → серия с метаданными
const _imageIds = new Map();   // seriesUID → string[] imageIds
const _metaCache = new Map();  // Cache для Volume Loader (3D MPR)

// ── [10.7.0] Два исследования одновременно ──────────────────────────
//
// Здесь была одна строка `clearWorkerData()` в начале registerWorkerResults,
// и из-за неё сравнить два исследования было физически нельзя. Она стирала не
// только список серий, но и `_metaCache` — геометрию каждого среза, которую
// отдаёт поставщик метаданных Cornerstone. После второй загрузки из первого
// исследования нельзя было построить даже плоскую стопку: кадры есть, а где
// они лежат в пространстве — уже неизвестно.
//
// Теперь память освобождается прицельно: вытесняется ЦЕЛОЕ исследование со
// всеми его сериями и всеми записями геометрии. Держим два — столько же, на
// сколько рассчитан раздел «Сравнение». Третье вытесняет самое давнее из тех,
// что не заняты в местах сравнения.
//
// Почему не больше двух. Каждое исследование держит запись геометрии на
// КАЖДЫЙ срез. Пять тысяч срезов — пять тысяч записей; два исследования это
// уже десять. Сами записи невелики, но за ними тянутся кадры в кэше
// Cornerstone, а это мегабайты. Предел в два — не техническая нужда, а
// осознанная граница: сколько окон в сравнении, столько исследований и живёт.
const MAX_STUDIES = 2;
const _studyOfSeries = new Map(); // seriesUID → studyUID
const _studyOrder = [];           // studyUID в порядке загрузки, самое давнее первым
let _keepStudies = [];            // занятые в местах сравнения — вытесняем последними

/**
 * [10.8.1] Сводка последней регистрации: сколько файлов пришло, сколько кадров
 * вышло, сколько отброшено как не-снимки.
 *
 * Появилась не для красоты. Две ошибки, найденные в 10.8.0, — кэш по имени
 * файла и падение разбора геометрии на двадцать первом срезе — обе выглядели
 * одинаково: файлов в папке больше, чем срезов в программе. Заметить это можно
 * было только пересчитав файлы руками. Врач не обязан этим заниматься:
 * посчитать должна программа и сказать вслух, если числа не сошлись.
 */
let _сводка = { files: 0, frames: 0, skipped: 0, at: 0 };
export const getLastRegisterStats = () => ({ ..._сводка });

/**
 * Убрать исследование целиком: его серии, кадры и записи геометрии.
 * Именно записи геометрии и есть причина, по которой это нельзя сделать общим
 * `clear()`: они лежат по imageId, а не по серии.
 */
function _вытеснитьИсследование(studyUID) {
  if (!studyUID) return 0;
  const серии = [];
  for (const [seriesUID, uid] of _studyOfSeries) if (uid === studyUID) серии.push(seriesUID);

  for (const seriesUID of серии) {
    for (const imageId of (_imageIds.get(seriesUID) || [])) _metaCache.delete(imageId);
    _imageIds.delete(seriesUID);
    _series.delete(seriesUID);
    _studyOfSeries.delete(seriesUID);
  }

  const i = _studyOrder.indexOf(studyUID);
  if (i >= 0) _studyOrder.splice(i, 1);
  return серии.length;
}

/**
 * Освободить место под то, что сейчас загрузят.
 *
 * Два разных случая, и путать их нельзя. Первый: грузят ТО ЖЕ исследование
 * заново — тогда старую копию надо снести, иначе серии удвоятся в списке.
 * Второй: грузят новое, а места нет — тогда сносим самое давнее.
 */
function _освободитьМесто(seriesMap) {
  const входящие = new Set(
    Object.values(seriesMap || {}).map((s) => s?.studyUID || 'unknown_study'),
  );

  // Перезагрузка того же исследования — заменяем, а не удваиваем.
  for (const uid of входящие) _вытеснитьИсследование(uid);

  const мест = Math.max(1, MAX_STUDIES - входящие.size);
  while (_studyOrder.length > мест) {
    // Занятые в сравнении отдаём последними: врач их видит на экране прямо
    // сейчас, и вытеснить их значило бы погасить у него окно.
    const жертва = _studyOrder.find((u) => !_keepStudies.includes(u)) ?? _studyOrder[0];
    const убрано = _вытеснитьИсследование(жертва);
    log.info(`[Bridge] исследование ${жертва} вытеснено (${убрано} серий): держим не больше ${MAX_STUDIES}`);
  }
}

async function getDicomParser() {
  if (_dicomParser) return _dicomParser;
  try { 
    const dpModule = await import('dicom-parser');
    _dicomParser = dpModule.default || dpModule; 
  } catch(e) { 
    log.debug('[LoaderWorkerBridge] Import fallback:', e.message); 
  }
  
  _dicomParser ??= dicomImageLoader?.external?.dicomParser ?? window.dicomParser;
  
  if (!_dicomParser) log.warn('[LoaderWorkerBridge] dicom-parser is completely unavailable!');
  return _dicomParser;
}

let _metaProviderRegistered = false;
function _ensureMetaProvider() {
  if (_metaProviderRegistered || !cornerstone?.metaData) return;
  
  try {
    // [CRITICAL FIX] Регистрируем только если Cornerstone готов
    // [7.0.57] То же, что в loaderManager: кадровый суффикс ?frame=N надо
    // отбрасывать, иначе кадры многокадрового файла остаются без геометрии.
    cornerstone.metaData.addProvider((type, imageId) => {
      const cached = _metaCache.get(imageId);
      if (cached) return cached[type];
      const base = String(imageId).split('?frame=')[0];
      return base !== imageId ? _metaCache.get(base)?.[type] : undefined;
    }, 20500);
    _metaProviderRegistered = true;
    log.debug('[WorkerBridge] metaData provider registered successfully.');
  } catch(e) {
    log.warn('[WorkerBridge] metaData provider registration deferred:', e.message);
  }
}

// ══════════════════════════════════════════════════════════════
// UTILS
// ══════════════════════════════════════════════════════════════

function registerFile(file) {
  const managers = [
    dicomImageLoader?.wadouri?.fileManager,
    window.cornerstoneWADOImageLoader?.wadouri?.fileManager,
    window.cornerstoneDICOMImageLoader?.wadouri?.fileManager
  ];

  for (const fm of managers) {
    if (fm?.add) return fm.add(file);
  }

  // Fallback
  return `wadouri:${URL.createObjectURL(file)}`;
}

/**
 * Вектор из тега DICOM — из строки «1\0\0\0\1\0» ИЛИ из уже готового массива.
 *
 * [10.8.0] Раньше здесь было только `str.split('\\')`, и это оказалось скрытой
 * миной. Разбор заголовка в программе идёт двумя дорогами: воркер и запасной
 * разборщик в основном потоке. Первая отдаёт геометрию строкой, вторая —
 * уже разобранным массивом чисел (см. parseNumArr в workerPool). Пока
 * метаданные читались только из файла, разницы не было. С 10.6.0 их стали
 * брать у воркера — и на массиве `split` падал с «t.split is not a function».
 *
 * Что при этом происходило на экране. Первые двадцать срезов серии
 * разбираются полностью, из файла, и проходили нормально. Начиная с
 * ДВАДЦАТЬ ПЕРВОГО метаданные брались у воркера — и каждый такой срез
 * отваливался с ошибкой в журнале. В серии из тридцати четырёх срезов
 * оставалось четыре. Ни одного сообщения врачу: просто серия короче, чем
 * файлов в папке, реформаты из огрызка, и всё это молча.
 *
 * Поймано не чтением кода, а замером: совмещение вернуло сдвиг 1,3 мм вместо
 * 8,8, и число выборок в журнале выдало объём в четыре среза вместо тридцати
 * четырёх. Отсюда и нашлась настоящая причина.
 */
const parseVector = (v, fallback) => {
  if (Array.isArray(v)) return v.length ? v.map(Number) : fallback;
  if (typeof v === 'string' && v.length) return v.split('\\').map(Number);
  return fallback;
};

// ══════════════════════════════════════════════════════════════
// DICOM PARSING
// ══════════════════════════════════════════════════════════════

async function parseAndCacheMeta(imageId, arrayBuffer) {
  const dp = await getDicomParser();
  if (!dp) return { instanceNum: 0, sliceLocation: 0, sliceThickness: 1, dataSet: null };

  // Парсим только до пиксельных данных (оптимизация памяти)
  const dataSet = dp.parseDicom(new Uint8Array(arrayBuffer), { untilTag: 'x7fe00010' });
  cacheDataSet(imageId, dataSet);

  const instanceNum = dataSet.intString('x00200013') || 0;
  // [10.19.1] Сырой тег — отдельно (см. loaderManager): протокол-контролю
  // нужна правда, а не дефолт.
  const sliceThicknessRaw = dataSet.floatString('x00180050');
  const sliceThickness = sliceThicknessRaw || 1;
  const rows = dataSet.uint16('x00280010') || 0;
  const cols = dataSet.uint16('x00280011') || 0;
  
  const bitsAllocated = dataSet.uint16('x00280100') || 16;
  const bitsStored = dataSet.uint16('x00280101') || bitsAllocated;
  const highBit = dataSet.uint16('x00280102') || (bitsStored - 1);
  const pixelRepresentation = dataSet.uint16('x00280103') || 0;
  const photometricInterpretation = dataSet.string('x00280004') || 'MONOCHROME2';
  const seriesUID = dataSet.string('x0020000e') || '';
  // [7.0.48 FIX] Настоящий Frame of Reference (0020,0052), а НЕ seriesUID.
  // seriesUID ≠ система координат: при подмене мультимодальная фьюжн-регистрация
  // по FOR и экспорт RTSTRUCT ссылаются «в никуда». Откат к seriesUID, если тега нет,
  // — поведение при отсутствии тега не меняется. Совпадает с логикой loaderManager.js.
  const frameOfReferenceUID = dataSet.string('x00200052') || seriesUID;

  const ipp = parseVector(dataSet.string('x00200032'), [0, 0, instanceNum * sliceThickness]);
  const iop = parseVector(dataSet.string('x00200037'), [1, 0, 0, 0, 1, 0]);
  const ps  = parseVector(dataSet.string('x00280030'), [1, 1]);

  _metaCache.set(imageId, {
    imagePlaneModule: {
      imagePositionPatient: ipp,
      imageOrientationPatient: iop,
      pixelSpacing: ps,
      rowPixelSpacing: ps[0],
      columnPixelSpacing: ps[1],
      sliceThickness,
      rows,
      columns: cols,
      frameOfReferenceUID,
    },
    imagePixelModule: {
      bitsAllocated, bitsStored, highBit, pixelRepresentation,
      photometricInterpretation, rows, columns: cols,
    },
  });

  return {
    instanceNum,
    sliceLocation: dataSet.floatString('x00201041') ?? 0,
    sliceThickness,
    sliceThicknessRaw,
    ipp,
    iop,
    nFrames: dataSet.intString('x00280008') || 1,
    dataSet,
  };
}

/**
 * [10.6.0] Метаданные ИЗ РАЗБОРА ВОРКЕРА — без повторного чтения файла.
 *
 * Каждый файл до сих пор читался дважды. Сначала его читал и разбирал воркер,
 * потом главный поток читал его заново, чтобы достать геометрию среза. На
 * компьютере второе чтение почти бесплатно: файл лежит в кэше файловой
 * системы. На Android это ещё одно обращение к системному поставщику
 * хранилища на КАЖДЫЙ срез — а их пять тысяч.
 *
 * Оказалось, что читать нечего: воркер и так разбирает все нужные теги —
 * положение среза (0020,0032), ориентацию (0020,0037), шаг пикселя (0028,0030),
 * толщину, размеры, битность, систему координат, число кадров. Мы просто ими
 * не пользовались.
 *
 * Здесь та же запись, что складывал парсер, но собранная из готового. Если
 * какого-то поля нет — возвращаем null, и вызывающий честно дочитает файл.
 */
function _метаИзВоркера(imageId, m) {
  if (!m) return null;
  const rows = +m.rows || 0;
  const cols = +m.columns || 0;
  if (!rows || !cols) return null;   // не снимок или разбор неполон — пусть читают файл

  const ipp = parseVector(m.imagePositionPatient, null);
  const iop = parseVector(m.imageOrientationPatient, null);
  if (!ipp || !iop) return null;     // без геометрии реформаты не построить

  // [12.0.22] ЗНАК ПИКСЕЛЕЙ НЕ УГАДЫВАЕМ. Здесь было «+m.pixelRepresentation
  // || 0»: нет поля — значит «без знака». Для знаковой КТ это ошибка на весь
  // снимок, и тихая: картинка есть, только белая. Нет поля или оно не 0/1 —
  // возвращаем null, и вызывающий честно дочитает файл разбором dicom-parser,
  // где поле читается двоичным числом. Ценой одного чтения файла.
  const знак = Number(m.pixelRepresentation);
  if (знак !== 0 && знак !== 1) return null;

  const sliceThickness = parseFloat(m.sliceThickness) || 1;
  const ps = parseVector(m.pixelSpacing, [1, 1]);
  const instanceNum = parseInt(m.instanceNumber, 10) || 0;
  const bitsAllocated = +m.bitsAllocated || 16;
  const bitsStored = +m.bitsStored || bitsAllocated;
  const seriesUID = m.seriesInstanceUID || '';

  _metaCache.set(imageId, {
    imagePlaneModule: {
      imagePositionPatient: ipp,
      imageOrientationPatient: iop,
      pixelSpacing: ps,
      rowPixelSpacing: ps[0],
      columnPixelSpacing: ps[1],
      sliceThickness,
      rows,
      columns: cols,
      frameOfReferenceUID: m.frameOfReferenceUID || seriesUID,
    },
    imagePixelModule: {
      bitsAllocated, bitsStored,
      highBit: (+m.highBit || (bitsStored - 1)),
      pixelRepresentation: знак,          // [12.0.22] проверен выше: 0 или 1
      photometricInterpretation: m.photometricInterpretation || 'MONOCHROME2',
      rows, columns: cols,
    },
  });

  return {
    instanceNum,
    sliceLocation: parseFloat(m.sliceLocation) || 0,
    sliceThickness,
    ipp,
    iop,
    nFrames: parseInt(m.numberOfFrames, 10) || 1,
    dataSet: null,     // полный разбор не нужен — см. ниже, он берётся у первых файлов серии
  };
}

// ══════════════════════════════════════════════════════════════
// MAIN API
// ══════════════════════════════════════════════════════════════

export async function registerWorkerResults(seriesMap, onProgress) {
  try {
    _ensureMetaProvider();
    // [10.7.0] Было `clearWorkerData()` — стирали всё подряд. Теперь освобождаем
    // ровно столько, сколько нужно под входящее: см. _освободитьМесто выше.
    _освободитьМесто(seriesMap);
  
    const seriesList = Object.values(seriesMap);
    const totalFiles = seriesList.reduce((acc, s) => acc + s.files.length, 0);
    let registered = 0;
  
    const _dataSetsPerPatient = new Map();
  
    // [10.3.1] Объекты без пикселей выбрасываем ПОФАЙЛЬНО, а не сериями.
    //
    // Зачем правило. В исследовании с ангиографа рядом со съёмкой лежат
    // Presentation State — «состояние показа»: как показывать кадры. Ни строк,
    // ни столбцов, ни пикселей в них нет. Раньше каждый заводился как отдельная
    // серия, и врач видел пять пустых строк «PR — 1 изобр.» вперемешку с
    // настоящими съёмками; нажатие давало чёрное окно.
    //
    // И вот на чём я обжёгся. Сперва я выбрасывал СЕРИЮ целиком, решая по
    // первому её файлу. На настоящем каталоге врача это убило саму съёмку:
    // в журнале стояло «Presentation State ×2» — то есть в одной серии
    // оказались и Presentation State, и файл XA. Так бывает, когда у файла не
    // прочитался Series UID и он попал в общую корзину (studyUID + '.1'), —
    // и тогда решение по ПЕРВОМУ файлу выносит приговор всей серии. В панели
    // осталась одна строка «Dose Report», а киносъёмки не было вовсе.
    //
    // Поэтому смотрим КАЖДЫЙ файл отдельно и убираем только его. Серия
    // исчезает лишь тогда, когда в ней не осталось ни одного снимка.
    const skipped = [];
    const imagingSeries = [];
    for (const s of seriesList) {
      const files = s.files || [];
      const keep = files.filter((f) => {
        const m = f?.meta || {};
        // Смотрим ТОЛЬКО на метаданные самого файла: поле серии могло прийти
        // от соседа по корзине и оболгать этот файл.
        const kind = classifySop({
          sopClassUID: m.sopClassUID,
          modality: m.modality,
          rows: m.rows, columns: m.columns,
        });
        if (!kind.isImage) skipped.push(kind.kind);
        return kind.isImage;
      });
      if (!keep.length) continue;                       // в серии одни не-снимки
      if (keep.length === files.length) { imagingSeries.push(s); continue; }

      // [10.3.1] Если из серии что-то выбросили, подпись берём у ОСТАВШЕГОСЯ
      // файла. Иначе выходит ложь с последствиями: у врача в панели стояло
      // «PR — Left Coronary 30 fps Low, 233 изобр.» — модальность от
      // выброшенного Presentation State, а кадры от киносъёмки. И это не
      // только надпись: по модальности решается, кино это или стопка срезов.
      // С чужим «PR» киносъёмка ушла бы в реформаты и снова дала
      // «Реформат: Infinity/Infinity».
      const firstMeta = keep[0]?.meta || {};
      imagingSeries.push({
        ...s,
        files: keep,
        modality: firstMeta.modality || s.modality,
        sopClassUID: firstMeta.sopClassUID || s.sopClassUID,
        seriesDesc: firstMeta.seriesDesc || s.seriesDesc,
      });
    }
    if (skipped.length) {
      const byKind = {};
      for (const k of skipped) byKind[k] = (byKind[k] || 0) + 1;
      log.info('[Bridge] не снимки, в список серий не попали: '
        + Object.entries(byKind).map(([k, n]) => `${k} — ${n}`).join('; '));
    }

    // Обработка серий последовательно, но ФАЙЛОВ внутри серии — параллельно (батчами)
    for (const s of imagingSeries) {
      const images = [];
      // [10.5.11] Было 50. Здесь время уходит не на счёт, а на ОЖИДАНИЕ ответа
      // хранилища: пока файл едет от системного поставщика, процессор простаивает.
      // Сто двадцать запросов в полёте вместо пятидесяти отнимают почти столько
      // же времени, сколько пятьдесят, — но успевают втрое больше. Память при
      // этом не страдает: читаем теперь по 16 КБ на файл, а не по 256.
      const BATCH_SIZE = 120;
      
      for (let i = 0; i < s.files.length; i += BATCH_SIZE) {
        const batch = s.files.slice(i, i + BATCH_SIZE);
        
        const batchPromises = batch.map(async (item, вБатче) => {
          const индексВСерии = i + вБатче;
          const imageId = registerFile(item.file);
          let geo = { instanceNum: item.meta?.instanceNumber || 0, sliceLocation: 0, sliceThickness: 1, dataSet: null };
          
          try {
            // [10.5.3] Читаем НАЧАЛО файла, а не файл целиком.
            //
            // Метаданные DICOM лежат до пиксельных данных, и разбор здесь и
            // так идёт с ограничением `untilTag: 'x7fe00010'` — то есть до
            // них. Значит остальные сотни килобайт читались впустую.
            //
            // На компьютере это было незаметно: файл только что прочитали
            // воркеры, он в кэше файловой системы, второе чтение — копия из
            // памяти. На Android каждый файл достаётся через системного
            // поставщика, отдельным обращением между процессами, и объём
            // имеет прямую цену.
            //
            // 256 КБ хватает с запасом: заголовок обычно единицы килобайт,
            // раздувают его только встроенные миниатюры (Icon Image Sequence)
            // — те самые, на которых мы уже спотыкались в 10.3.0. Если в этот
            // кусок разбор не уложился, честно дочитываем файл целиком.
            // [10.5.11] Читаем по возрастающей: сначала маленький кусок.
            //
            // Заголовок DICOM почти всегда укладывается в единицы килобайт —
            // это имя, дата, геометрия среза, параметры съёмки. Раздувают его
            // только встроенные миниатюры (Icon Image Sequence), и такие файлы
            // редки. Читать всем подряд по 256 КБ ради редкого случая — платить
            // за него каждым срезом; на телефоне каждый лишний килобайт идёт
            // через системного поставщика хранилища.
            //
            // Поэтому: пробуем 16 КБ; не хватило — 256 КБ; не хватило и их —
            // читаем файл целиком. Разбор сам сообщает о нехватке исключением,
            // гадать не приходится. Для подавляющего большинства срезов
            // достаточно первой попытки.
            // [10.6.0] Сначала — то, что воркер уже разобрал. Файл читаем
            // только если готового не хватило, либо это один из первых файлов
            // серии: с них берём полный разбор для окна DICOM-тегов и оценки
            // качества. Двадцати штук на серию для этого достаточно, а пять
            // тысяч чтений превращаются в двадцать.
            const нуженПолныйРазбор = (индексВСерии < 20);
            let geoГотово = false;
            if (!нуженПолныйРазбор) {
              const готовое = _метаИзВоркера(imageId, item.meta);
              if (готовое) { geo = готовое; geoГотово = true; }
            }
            if (!geoГотово) for (const кусок of [16384, 262144, 0]) {
              try {
                const ab = кусок
                  ? await item.file.slice(0, кусок).arrayBuffer()
                  : await item.file.arrayBuffer();
                geo = await parseAndCacheMeta(imageId, ab);
                geoГотово = true;
                break;
              } catch (eКусок) { /* мало — берём следующий размер */ }
            }
            if (!geoГотово) throw new Error('заголовок не разобран ни одним размером');
  
            // Собираем первый валидный dataSet для каждого пациента (Quality Analysis)
            const patKey = (s.patientId || s.patientName || 'UNKNOWN').trim();
            if (!_dataSetsPerPatient.has(patKey) && geo.dataSet) {
              _dataSetsPerPatient.set(patKey, {
                name: s.patientName || '',
                id: s.patientId || '',
                sex: geo.dataSet.string('x00100040') || '',
                birth: geo.dataSet.string('x00100030') || '',
                dataSet: geo.dataSet,
                серии: new Map(),
              });
            }
            // [12.0.24] И по первому набору на КАЖДУЮ серию. Прежде отчёт о
            // качестве строился по первому файлу пациента — одной серии на всё
            // исследование: у КТ с нативной «Body 1.0» и тремя «Body 1.0 CE»
            // он описывал нативную, какую бы серию врач ни смотрел.
            const пац = _dataSetsPerPatient.get(patKey);
            const серия = s.seriesUID || '';
            if (пац && geo.dataSet && серия && !пац.серии.has(серия)) {
              пац.серии.set(серия, { uid: серия, описание: s.seriesDesc || '', dataSet: geo.dataSet });
            }
          } catch (e) {
            log.warn(`[Bridge] Meta parse error for ${item.file?.name}:`, e.message);
          }
  
          registered++;
          if (onProgress) onProgress(registered, totalFiles);
  
          // [MR] Многокадровый файл — каждый кадр становится отдельным срезом (?frame= с единицы)
          if ((geo.nFrames || 1) > 1) {
            const entries = [];
            for (let f = 1; f <= geo.nFrames; f++) {
              entries.push({
                imageId: `${imageId}?frame=${f}`,
                instanceNum: (geo.instanceNum || 0) * 100000 + f,
                sliceLocation: 0,
                sliceThickness: geo.sliceThickness,
                sliceThicknessRaw: geo.sliceThicknessRaw,
                _multiframe: true,
              });
            }
            return entries;
          }

          return {
            imageId,
            instanceNum: geo.instanceNum,
            sliceLocation: geo.sliceLocation,
            sliceThickness: geo.sliceThickness,
            sliceThicknessRaw: geo.sliceThicknessRaw,
            ipp: geo.ipp,
            iop: geo.iop,
          };
        });
  
        // Ждем завершения батча перед следующим (.flat — многокадровые вернули массивы)
        images.push(...(await Promise.all(batchPromises)).flat());
      }
  
      // Точная сортировка MPR: по координате вдоль нормали, fallback — прежнее поведение
      sortSlices(images);
  
      // [10.3.0] Помечаем киносъёмку.
      //
      // У ангиографии все 233 кадра сняты С ОДНОГО положения трубки — это
      // один и тот же вид в разные моменты времени, а не стопка срезов по
      // телу. Размах положений равен нулю, поэтому объём и реформаты из такой
      // серии не строятся: на экране выходило «Реформат: Infinity/Infinity»
      // и три чёрных окна из четырёх. Пометка нужна, чтобы автоматика в
      // реформаты такую серию не уводила.
      const zs = images.map(i => (Array.isArray(i.ipp) ? i.ipp[2] : null)).filter(Number.isFinite);
      const spread = zs.length >= 2 ? Math.max(...zs) - Math.min(...zs) : null;
      const cine = classifyCine({ modality: s.modality, numberOfFrames: images.length, spatialSpreadMm: spread });
      if (cine.isCine) log.info(`[Bridge] серия «${s.seriesDesc || s.modality}» — киносъёмка: ${cine.why}`);

      // [10.8.0] Сколько файлов пришло и сколько кадров вышло. Если числа
      // расходятся — часть срезов потерялась молча, и это надо видеть.
      if (images.length !== s.files.length) {
        log.warn(`[Bridge] серия «${s.seriesDesc || s.modality}»: файлов ${s.files.length}, кадров вышло ${images.length}`);
      }
      _imageIds.set(s.seriesUID, images.map(img => img.imageId));
      _series.set(s.seriesUID, { ...s, images, isCine: cine.isCine, cineWhy: cine.why });

      // [10.7.0] Запоминаем, чьё это исследование. Без этой строчки вытеснение
      // не знает, что убирать, и пришлось бы опять сносить всё разом.
      const studyUID = s.studyUID || 'unknown_study';
      _studyOfSeries.set(s.seriesUID, studyUID);
      if (!_studyOrder.includes(studyUID)) _studyOrder.push(studyUID);
    }
  
    // Кадры считаем только по сериям ЭТОЙ регистрации: в _series может лежать
    // и второе исследование, и его срезы к нынешней папке отношения не имеют.
    const кадровСейчас = imagingSeries.reduce(
      (сумма, s) => сумма + ((_series.get(s.seriesUID)?.images || []).length), 0);
    _сводка = { files: totalFiles, frames: кадровСейчас, skipped: skipped.length, at: Date.now() };
    if (кадровСейчас + skipped.length < totalFiles) {
      log.warn(`[Bridge] ПОТЕРЯ: файлов ${totalFiles}, кадров ${кадровСейчас}, не-снимков ${skipped.length}`);
    }
    log.debug(`[Bridge] Registered ${registered} files, ${_series.size} series, metaCache: ${_metaCache.size}`);
    
    // Запускаем анализ качества асинхронно, чтобы не тормозить UI
    setTimeout(() => _runQualityAnalysis(_dataSetsPerPatient), 0);
  
    return getWorkerSeries();
  } catch (e) { log.warn(`[loaderWorkerBridge] registerWorkerResults failed:`, e.message); return {}; }
}

export const getWorkerSeries = () => [..._series.values()];
export const getWorkerSeriesImageIds = (seriesUID) => _imageIds.get(seriesUID) || [];
export const hasWorkerData = () => _series.size > 0;
export const clearWorkerData = () => {
  _series.clear(); _imageIds.clear(); _metaCache.clear();
  _studyOfSeries.clear(); _studyOrder.length = 0; _keepStudies = [];
};

// ── [10.7.0] Доступ к исследованиям снаружи ──────────────────────────

/** Сколько исследований держим одновременно. Знать это нужно и разделу «Сравнение». */
export const getMaxStudies = () => MAX_STUDIES;

/** Номера загруженных исследований в порядке загрузки, самое давнее первым. */
export const getWorkerStudyUIDs = () => [..._studyOrder];

/** Какому исследованию принадлежит серия. */
export const getStudyOfSeries = (seriesUID) => _studyOfSeries.get(seriesUID) || null;

/** Серии одного исследования — в том порядке, в каком они зарегистрированы. */
export const getStudySeries = (studyUID) =>
  [..._series.values()].filter((s) => (_studyOfSeries.get(s.seriesUID) || 'unknown_study') === studyUID);

/**
 * Пометить исследования как занятые: их вытесняют последними.
 * Раздел «Сравнение» зовёт это, когда врач кладёт исследование в место.
 */
export const setKeepStudies = (uids) => { _keepStudies = Array.isArray(uids) ? uids.filter(Boolean) : []; };

/** Убрать исследование по требованию — например, когда врач очистил место. */
export const forgetStudy = (studyUID) => _вытеснитьИсследование(studyUID);

// ══════════════════════════════════════════════════════════════
// QUALITY ANALYSIS
// ══════════════════════════════════════════════════════════════

/**
 * [12.0.24] Анализ качества — по одному на серию, с пометкой, какая это серия.
 * Нет разбивки по сериям (старые записи) — один анализ по набору пациента,
 * как было.
 */
function _анализыПоСериям(pat, запасные) {
  const серии = pat.серии && pat.серии.size ? [...pat.серии.values()] : null;
  if (!серии) return [StudyQualityAnalyzer.analyze(запасные)];
  return серии.map((с) => {
    const а = StudyQualityAnalyzer.analyze(StudyQualityAnalyzer.extract(с.dataSet));
    а.серия = { uid: с.uid, описание: с.описание };
    return а;
  });
}

function _runQualityAnalysis(dataSetsPerPatient) {
  if (!dataSetsPerPatient.size) return;

  try {
    const patientsMap = {};
    for (const [key, pat] of dataSetsPerPatient) {
      const params = StudyQualityAnalyzer.extract(pat.dataSet);
      patientsMap[key] = {
        name: pat.name || params.patientName,
        id: pat.id || params.patientId,
        sex: pat.sex,
        birth: pat.birth,
        analyses: _анализыПоСериям(pat, params),   // [12.0.24] по одному на серию
      };
    }

    const patientKeys = Object.keys(patientsMap);
    const isMulti = patientKeys.length > 1;

    _setQualityState(patientsMap, isMulti ? patientsMap : patientsMap[patientKeys[0]]?.analyses[0]);

    // [11.1.6] ЗНАЧОК КАЧЕСТВА УБРАН (см. loaderManager: то же решение
    // владельца). Здесь была вторая, независимая копия того же значка — путь
    // воркера строил его сам. Оставлен только счётчик пациентов: он про
    // безопасность, а не про качество — предупреждает, что в папке разные
    // больные, и другого такого предупреждения в программе нет.
    const statusEl = document.getElementById('status-bar') ?? document.getElementById('dicom-study-info');
    if (statusEl) {
      document.getElementById('quality-badge')?.remove();
      if (isMulti) {
        const qSpan = document.createElement('span');
        qSpan.id = 'quality-badge';
        qSpan.style.cssText = 'cursor:pointer; margin-left:8px; font-weight:bold; transition: opacity 0.2s;';
        qSpan.title = 'В загруженной папке несколько разных пациентов. Щёлкните, чтобы посмотреть список';
        qSpan.innerHTML = ` ${patientsBadgeHTML(patientKeys.length)}`;
        qSpan.onclick = () => showStudyQuality();
        qSpan.onmouseenter = () => { qSpan.style.opacity = '0.7'; };
        qSpan.onmouseleave = () => { qSpan.style.opacity = '1'; };
        statusEl.appendChild(qSpan);
      }
    }
  } catch (e) {
    log.error('[Quality] Analysis failed:', e);
  }
}
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Meta cache stats, diagnostics
// ══════════════════════════════════════════════════════════════

/**
 * Get meta cache statistics (for 3D MPR geometry data)
 * @returns {{size:number, estimatedKB:number, providerRegistered:boolean}}
 */
export function getMetaCacheStats() {
  return {
    size: _metaCache.size,
    estimatedKB: +(_metaCache.size * 0.2).toFixed(0),
    providerRegistered: _metaProviderRegistered,
    seriesCount: _series.size,
    totalImageIds: [..._imageIds.values()].reduce((sum, ids) => sum + ids.length, 0),
  };
}

/**
 * Validate that all imageIds have meta cache entries
 * @returns {{total:number, withMeta:number, missing:number, missingIds:string[]}}
 */
export function validateMetaCache() {
  let total = 0, withMeta = 0;
  const missingIds = [];
  for (const [, ids] of _imageIds) {
    for (const id of ids) {
      total++;
      if (_metaCache.has(id)) withMeta++;
      else missingIds.push(id);
    }
  }
  return { total, withMeta, missing: total - withMeta, missingIds: missingIds.slice(0, 10) };
}

/**
 * Get worker bridge diagnostics
 * @returns {Object}
 */
export function getWorkerBridgeState() {
  return {
    series: _series.size,
    imageIds: [..._imageIds.values()].reduce((s, ids) => s + ids.length, 0),
    metaCache: _metaCache.size,
    metaProvider: _metaProviderRegistered,
    dicomParserLoaded: !!_dicomParser,
  };
}
